import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  CircularProgress,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import axios from "axios";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import config from "../../config.js";
import DeleteModal from "../common/DeleteModal";
import { isRouteEyeAdmin } from "../../utils/isRouteEyeAdmin";
import { useUser } from "../../pages/login/UserContextPersistent";

const LABEL_TYPES = [
  "BUS_STAND",
  "FUEL_STATION",
  "MOTELS",
  "CHARGING_STATION",
  "RAILWAY_STATION",
  "HOSPITAL",
  "RESTAURANT",
  "PARKING",
  "CAFETERIA",
  "GATE",
  "DROP_POINT",
  "PICKUP_POINT",
  "DRIVER",
  "USER",
  "PARENT",
  "EMPLOYEE",
];

const TYPE_COLORS = {
  BUS_STAND: "#1976d2",
  FUEL_STATION: "#f57c00",
  MOTELS: "#7b1fa2",
  CHARGING_STATION: "#388e3c",
  RAILWAY_STATION: "#d32f2f",
  HOSPITAL: "#c62828",
  RESTAURANT: "#f9a825",
  PARKING: "#455a64",
  CAFETERIA: "#e64a19",
  GATE: "#00796b",
  DROP_POINT: "#1565c0",
  PICKUP_POINT: "#6a1b9a",
  DRIVER: "#4527a0",
  USER: "#00695c",
  PARENT: "#2e7d32",
  EMPLOYEE: "#1565c0",
};

const EMPTY_FORM = { name: "", type: "", description: "" };

const headerCellSx = {
  fontWeight: 600,
  fontSize: "16px",
  color: "#fff",
  background: "#647490",
  borderBottom: "none",
  py: 1.5,
  px: 2,
};

const bodyCellSx = {
  fontSize: "16px",
  color: "#1F242E",
  py: 1.5,
  px: 2,
  borderBottom: "1px solid #e0e0e0",
};

// Shared input sx — matches SettingRow / SettingTextRow in appConstants
const inputSx = {
  "& .MuiOutlinedInput-root": {
    height: "48px",
    borderRadius: "8px",
    backgroundColor: "#FFFFFF",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#E0E0E0",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#A5DCF3",
  },
};

const selectSx = {
  height: "48px",
  borderRadius: "8px",
  backgroundColor: "#FFFFFF",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#E0E0E0",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#A5DCF3",
  },
};

export default function Labels() {
  const { t } = useTranslation();
  const { organizationInactive } = useUser();
  const canMutate = !organizationInactive;

  const [labels, setLabels] = useState([]);
  const [loading, setLoading] = useState(true);

  // inline create row
  const [showCreateRow, setShowCreateRow] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_FORM);
  const [creating, setCreating] = useState(false);

  // inline edit row
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  // delete modal
  const [deleteModal, setDeleteModal] = useState({
    open: false,
    id: null,
    name: "",
  });
  const [deleting, setDeleting] = useState(false);

  // ─── fetch ──────────────────────────────────────────────────────────────────
  const fetchLabels = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${config.baseURL}/label`, {
        withCredentials: true,
      });
      const data = res.data;
      setLabels(
        Array.isArray(data) ? data : (data?.labels ?? data?.data ?? []),
      );
    } catch (err) {
      console.error("Failed to fetch labels:", err);
      toast.error(t("Failed to load labels"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchLabels();
  }, [fetchLabels]);

  // ─── create ──────────────────────────────────────────────────────────────────
  const handleCreateOpen = () => {
    if (!canMutate) return;
    setCreateForm(EMPTY_FORM);
    setShowCreateRow(true);
  };

  const handleCreateCancel = () => {
    setShowCreateRow(false);
    setCreateForm(EMPTY_FORM);
  };

  const handleCreateChange = (field) => (e) =>
    setCreateForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleCreateSave = async () => {
    if (!canMutate) return;
    if (!createForm.name.trim()) {
      toast.warning(t("Label name is required"));
      return;
    }
    if (!createForm.type) {
      toast.warning(t("Label type is required"));
      return;
    }
    setCreating(true);
    try {
      const payload = {
        name: createForm.name.trim(),
        type: createForm.type,
        ...(createForm.description.trim() && {
          description: createForm.description.trim(),
        }),
      };
      await axios.post(`${config.baseURL}/label`, payload, {
        withCredentials: true,
      });
      toast.success(t("Label created successfully"));
      setShowCreateRow(false);
      setCreateForm(EMPTY_FORM);
      fetchLabels();
    } catch (err) {
      console.error("Create label error:", err);
      toast.error(err.response?.data?.message || t("Failed to create label"));
    } finally {
      setCreating(false);
    }
  };

  // ─── edit ────────────────────────────────────────────────────────────────────
  const handleEditOpen = (label) => {
    if (!canMutate) return;
    setEditingId(label.id);
    setEditForm({
      name: label.name || "",
      type: label.type || "",
      description: label.description || "",
    });
  };

  const handleEditCancel = () => {
    setEditingId(null);
    setEditForm(EMPTY_FORM);
  };

  const handleEditChange = (field) => (e) =>
    setEditForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleEditSave = async () => {
    if (!canMutate) return;
    if (!editForm.name.trim()) {
      toast.warning(t("Label name is required"));
      return;
    }
    if (!editForm.type) {
      toast.warning(t("Label type is required"));
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: editForm.name.trim(),
        type: editForm.type,
        description: editForm.description.trim(),
      };
      await axios.put(`${config.baseURL}/label/${editingId}`, payload, {
        withCredentials: true,
      });
      toast.success(t("Label updated successfully"));
      setEditingId(null);
      setEditForm(EMPTY_FORM);
      fetchLabels();
    } catch (err) {
      console.error("Update label error:", err);
      toast.error(err.response?.data?.message || t("Failed to update label"));
    } finally {
      setSaving(false);
    }
  };

  // ─── delete ──────────────────────────────────────────────────────────────────
  const handleDeleteOpen = (label) => {
    if (!canMutate) return;
    setDeleteModal({ open: true, id: label.id, name: label.name });
  };

  const handleDeleteClose = () => {
    setDeleteModal({ open: false, id: null, name: "" });
  };

  const handleDeleteConfirm = async () => {
    if (!canMutate) return;
    setDeleting(true);
    try {
      await axios.delete(`${config.baseURL}/label/${deleteModal.id}`, {
        withCredentials: true,
      });
      toast.success(t("Label deleted successfully"));
      handleDeleteClose();
      fetchLabels();
    } catch (err) {
      console.error("Delete label error:", err);
      toast.error(err.response?.data?.message || t("Failed to delete label"));
    } finally {
      setDeleting(false);
    }
  };

  // ─── helpers ─────────────────────────────────────────────────────────────────
  const TypeChip = ({ type }) => (
    <Chip
      label={type
        ?.replace(/_/g, " ")
        .toLowerCase()
        .replace(/\b\w/g, (c) => c.toUpperCase())}
      size="small"
      sx={{
        backgroundColor: TYPE_COLORS[type] || "#647490",
        color: "#fff",
        fontWeight: 600,
        fontSize: "11px",
        height: "24px",
      }}
    />
  );

  const InlineTypeSelect = ({ value, onChange, disabled }) => (
    <FormControl fullWidth disabled={disabled}>
      <Select value={value} onChange={onChange} displayEmpty sx={selectSx}>
        <MenuItem value="" disabled>
          <em>{t("Select Type")}</em>
        </MenuItem>
        {LABEL_TYPES.map((lt) => (
          <MenuItem key={lt} value={lt}>
            {lt
              .replace(/_/g, " ")
              .toLowerCase()
              .replace(/\b\w/g, (c) => c.toUpperCase())}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  // ─── render ──────────────────────────────────────────────────────────────────
  if (!isRouteEyeAdmin()) {
    return <></>;
  }

  return (
    <Box>
      {/* ── Header row ── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          //   justifyContent: "space-between",
          mb: 2,
          gap: 2,
        }}
      >
        <Typography
          sx={{
            fontSize: "20px",
            fontWeight: 700,
            color: "#1F242E",
          }}
        >
          {t("Labels")}
        </Typography>

        {canMutate && (
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateOpen}
          disabled={showCreateRow}
          sx={{
            backgroundColor: "#20A8E0",
            color: "#fff",
            textTransform: "none",
            fontWeight: 600,
            fontSize: "15px",
            borderRadius: "8px",
            height: "40px",
            px: 2.5,
            "&:hover": { backgroundColor: "#1a90c4" },
            "&.Mui-disabled": { backgroundColor: "#b0d8ef", color: "#fff" },
          }}
        >
          {t("Create Label")}
        </Button>
        )}
      </Box>

      {/* ── Table ── */}
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          border: "1px solid #C8CBD0",
          borderRadius: "10px",
          overflow: "hidden",
        }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...headerCellSx, borderTopLeftRadius: "10px" }}>
                {t("Label Title")}
              </TableCell>
              <TableCell sx={headerCellSx}>{t("Type")}</TableCell>
              <TableCell sx={headerCellSx}>{t("Description")}</TableCell>
              <TableCell
                sx={{
                  ...headerCellSx,
                  borderTopRightRadius: "10px",
                  textAlign: "right",
                }}
              >
                {t("Actions")}
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {/* ── Inline create row ── */}
            {showCreateRow && (
              <TableRow sx={{ backgroundColor: "#f0f9ff" }}>
                <TableCell sx={bodyCellSx}>
                  <TextField
                    placeholder={t("Label name")}
                    value={createForm.name}
                    onChange={handleCreateChange("name")}
                    disabled={creating}
                    autoFocus
                    fullWidth
                    sx={inputSx}
                  />
                </TableCell>
                <TableCell sx={bodyCellSx}>
                  <InlineTypeSelect
                    value={createForm.type}
                    onChange={handleCreateChange("type")}
                    disabled={creating}
                  />
                </TableCell>
                <TableCell sx={bodyCellSx}>
                  <TextField
                    placeholder={t("Description (optional)")}
                    value={createForm.description}
                    onChange={handleCreateChange("description")}
                    disabled={creating}
                    fullWidth
                    sx={inputSx}
                  />
                </TableCell>
                <TableCell sx={{ ...bodyCellSx, textAlign: "right" }}>
                  <Tooltip title={t("Save")}>
                    <span>
                      <IconButton
                        size="small"
                        onClick={handleCreateSave}
                        disabled={creating}
                        sx={{ color: "#388e3c" }}
                      >
                        {creating ? (
                          <CircularProgress size={18} />
                        ) : (
                          <CheckIcon fontSize="small" />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={t("Cancel")}>
                    <IconButton
                      size="small"
                      onClick={handleCreateCancel}
                      disabled={creating}
                      sx={{ color: "#d32f2f" }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            )}

            {/* ── Loading skeleton rows ── */}
            {loading &&
              [...Array(4)].map((_, i) => (
                <TableRow key={`skeleton-${i}`}>
                  {[...Array(4)].map((__, j) => (
                    <TableCell key={j} sx={bodyCellSx}>
                      <Box
                        sx={{
                          height: 20,
                          borderRadius: 1,
                          backgroundColor: "#e0e0e0",
                          animation: "pulse 1.5s ease-in-out infinite",
                          "@keyframes pulse": {
                            "0%, 100%": { opacity: 1 },
                            "50%": { opacity: 0.4 },
                          },
                        }}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              ))}

            {/* ── Data rows ── */}
            {!loading &&
              labels.map((label) =>
                editingId === label.id ? (
                  /* ── Edit row ── */
                  <TableRow key={label.id} sx={{ backgroundColor: "#fffde7" }}>
                    <TableCell sx={bodyCellSx}>
                      <TextField
                        value={editForm.name}
                        onChange={handleEditChange("name")}
                        disabled={saving}
                        autoFocus
                        fullWidth
                        sx={inputSx}
                      />
                    </TableCell>
                    <TableCell sx={bodyCellSx}>
                      <InlineTypeSelect
                        value={editForm.type}
                        onChange={handleEditChange("type")}
                        disabled={saving}
                      />
                    </TableCell>
                    <TableCell sx={bodyCellSx}>
                      <TextField
                        value={editForm.description}
                        onChange={handleEditChange("description")}
                        disabled={saving}
                        fullWidth
                        sx={inputSx}
                      />
                    </TableCell>
                    <TableCell sx={{ ...bodyCellSx, textAlign: "right" }}>
                      <Tooltip title={t("Save")}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={handleEditSave}
                            disabled={saving}
                            sx={{ color: "#388e3c" }}
                          >
                            {saving ? (
                              <CircularProgress size={18} />
                            ) : (
                              <CheckIcon fontSize="small" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={t("Cancel")}>
                        <IconButton
                          size="small"
                          onClick={handleEditCancel}
                          disabled={saving}
                          sx={{ color: "#d32f2f" }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ) : (
                  /* ── Normal row ── */
                  <TableRow
                    key={label.id}
                    hover
                    sx={{
                      "&:hover": { backgroundColor: "#f5f8fc" },
                      "&:last-child td": { borderBottom: "none" },
                    }}
                  >
                    <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>
                      {label.name}
                    </TableCell>
                    <TableCell sx={bodyCellSx}>
                      <TypeChip type={label.type} />
                    </TableCell>
                    <TableCell sx={{ ...bodyCellSx, color: "#647490" }}>
                      {label.description || (
                        <Typography
                          component="span"
                          sx={{
                            fontSize: "13px",
                            color: "#aaa",
                            fontStyle: "italic",
                          }}
                        >
                          {t("No description")}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ ...bodyCellSx, textAlign: "right" }}>
                      {canMutate && (
                        <>
                      <Tooltip title={t("Edit")}>
                        <IconButton
                          size="small"
                          onClick={() => handleEditOpen(label)}
                          disabled={editingId !== null || showCreateRow}
                          sx={{ color: "#20A8E0", mr: 0.5 }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t("Delete")}>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteOpen(label)}
                          disabled={editingId !== null || showCreateRow}
                          sx={{ color: "#d32f2f" }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ),
              )}

            {/* ── Empty state ── */}
            {!loading && labels.length === 0 && !showCreateRow && (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
                  <Typography sx={{ color: "#aaa", fontSize: "15px" }}>
                    {t('No labels found. Click "Create Label" to add one.')}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ── Delete confirmation modal ── */}
      <DeleteModal
        isOpen={deleteModal.open}
        onClose={handleDeleteClose}
        onConfirm={handleDeleteConfirm}
        title={t("Delete Label")}
        message={`${t("Are you sure you want to delete the label")} "${deleteModal.name}"? ${t("This action cannot be undone.")}`}
        confirmButtonText={deleting ? t("Deleting…") : t("Delete")}
        titleColor="#EE3B2B"
        confirmButtonColor="#EE3B2B"
      />
    </Box>
  );
}
